import { exhaustiveMatchingGuard } from '@viilu-web/shared/util/exhaustiveMatchingGuard'
import { LoanEventType } from '@viilu-web/shared/enums/loans'
import type { CreateLoanEventInput } from '../types'
import { loanEventTypeOptions } from '../utils'

export const createEventInputs: CreateLoanEventInput[] = [
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Tapahtuman tyyppi',
				description: 'Event type',
			}),
		key: 'type',
		fullWidth: true,
		show: true,
		type: 'select',
		menuItems: (intl) => {
			return intl ? loanEventTypeOptions(intl) : []
		},
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Tapahtumapäivä',
				description: 'Event date',
			}),
		key: 'eventDate',
		show: true,
		type: 'date',
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Kirjauspäivä',
				description: 'Register date',
			}),
		key: 'registerDate',
		show: true,
		type: 'date',
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Osakeryhmien lyhennykset',
				description: 'Sharegroup repayments',
			}),
		key: 'sharegroupDebtRepayments',
		fullWidth: true,
		show: false,
		showOnCondition: (watch) =>
			watch('type') === LoanEventType.COLLECTED_SHAREGROUP_BANK_REPAYMENT,
		type: 'event_table',
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Huoneisto',
				description: 'Apartment ',
			}),
		key: 'shareGroupUUID',
		fullWidth: true,
		show: false,
		showOnCondition: (watch) =>
			watch('type') === LoanEventType.SHAREGROUP_DEBT_REPAYMENT,
		type: 'select',
		menuItems: (intl, items) => items || [],
	},
	{
		title: (intl, value) => {
			const eventType = value as LoanEventType
			switch (eventType) {
				case LoanEventType.LOAN_DRAW:
					return intl.formatMessage({
						defaultMessage: 'Lainan nostosumma (€)',
						description: 'Loan draw amount',
					})
				case LoanEventType.LOAN_BANK_REPAYMENT:
					return intl.formatMessage({
						defaultMessage: 'Lyhennyksen määrä (€)',
						description: 'Repayment amount',
					})
				case LoanEventType.INTEREST_CHANGE:
					return intl.formatMessage({
						defaultMessage: 'Korkomäärä (%)',
						description: 'Interest amount',
					})
				case LoanEventType.COLLECTED_SHAREGROUP_BANK_REPAYMENT:
					return intl.formatMessage({
						defaultMessage: 'Yhteensä lyhennyksen määrä',
						description: 'Total repayment amount',
					})
				case LoanEventType.SHAREGROUP_DEBT_REPAYMENT:
					return intl.formatMessage({
						defaultMessage: 'Suorituksen määrä (€)',
						description: 'Repayment amount',
					})
				case LoanEventType.STARTING_BALANCE:
					return intl.formatMessage({
						defaultMessage: 'Saldo',
						description: 'Balance',
					})
				case LoanEventType.STARTING_COLLECTED_PAYMENTS:
					return intl.formatMessage({
						defaultMessage: 'Vastikesumma',
						description: 'Payments sum',
					})
				case LoanEventType.STARTING_SURPLUS_OR_DEFICIT:
					return intl.formatMessage({
						defaultMessage: 'Yli- tai alijäämä',
						description: 'Surplus or deficit',
					})
				default:
					return exhaustiveMatchingGuard(
						eventType,
						intl.formatMessage({
							defaultMessage: 'Määrä',
							description: 'Amount',
						}),
					)
			}
		},
		key: 'amount',
		fullWidth: true,
		show: true,
		type: 'number',
		min: (eventType) => {
			switch (eventType) {
				case LoanEventType.LOAN_DRAW:
					return 0
				case LoanEventType.LOAN_BANK_REPAYMENT:
					return 0
				case LoanEventType.INTEREST_CHANGE:
					return undefined
				case LoanEventType.COLLECTED_SHAREGROUP_BANK_REPAYMENT:
					return 0
				case LoanEventType.SHAREGROUP_DEBT_REPAYMENT:
					return 0
				case LoanEventType.STARTING_BALANCE:
					return undefined
				case LoanEventType.STARTING_COLLECTED_PAYMENTS:
					return 0
				case LoanEventType.STARTING_SURPLUS_OR_DEFICIT:
					return undefined
				default:
					return exhaustiveMatchingGuard(eventType, undefined)
			}
		},
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Selite',
				description: 'Explanation',
			}),
		key: 'explanation',
		fullWidth: true,
		show: true,
		type: 'textArea',
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Pankkikulut',
				description: 'Bank expense amount',
			}),
		key: 'bankExpenseAmount',
		fullWidth: false,
		show: true,
		type: 'number',
		min: 0,
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Muut kulut',
				description: 'Other expense amount',
			}),
		key: 'otherExpenseAmount',
		fullWidth: false,
		show: true,
		type: 'number',
		min: 0,
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Koron määrä',
				description: 'Interest amount',
			}),
		key: 'interestAmount',
		fullWidth: true,
		show: false,
		type: 'number',
		min: 0,
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Korko alkaa',
				description: 'Interest start date',
			}),
		key: 'interestStartDate',
		show: false,
		type: 'date',
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Korko päättyy',
				description: 'Interest end date',
			}),
		key: 'interestEndDate',
		show: false,
		type: 'date',
	},
]

export const loanEventTableHeaders = [
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Tapahtuman päivämäärä',
				description: 'Table header for event date',
			}),
		key: 'eventDate',
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Tyyppi',
				description: 'Table header for type',
			}),
		key: 'type',
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Summa',
				description: 'Table header for sum',
			}),
		key: 'amount',
	},
]

export const loanEventCreateDialogSharegroupTableHeaders = [
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Tap. pvm',
				description: 'Table header for event date',
			}),
		key: 'eventDate',
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Kirj. pvm',
				description: 'Table header for register date',
			}),
		key: 'registerDate',
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Huoneisto',
				description: 'Table header for event date',
			}),
		key: 'apartmentIdentifier',
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Lainaosuus',
				description: 'Table header for type',
			}),
		key: 'shareAmount',
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Lyhennys',
				description: 'Table header for sum',
			}),
		key: 'amount',
	},
	{
		title: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Yksiköt',
				description: 'Table header for units',
			}),
		key: 'units',
	},
]

export const loanShareCalculationBlocks = [
	{
		blockTitle: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Tuotot ja kulut',
				description: 'Income and expenses',
			}),
		key: 'income-and-expenses',
		rows: [
			{
				title: (intl) =>
					intl.formatMessage({
						defaultMessage: 'Lainaa nostettu',
						description: 'Loan amount drawn',
					}),
				key: 'loanDrawnAmount',
			},
			{
				title: (intl) =>
					intl.formatMessage({
						defaultMessage: 'Saldo',
						description: 'Balance amount',
					}),
				key: 'balance',
			},
			{
				title: (intl) =>
					intl.formatMessage({
						defaultMessage: 'Vastiketuotot',
						description: 'Payment income',
					}),
				key: 'paymentIncome',
			},
			{
				title: (intl) =>
					intl.formatMessage({
						defaultMessage: 'Muut tuotot',
						description: 'Other income',
					}),
				key: 'otherIncome',
			},
			{
				title: (intl) =>
					intl.formatMessage({
						defaultMessage: 'Lyhennykset',
						description: 'Repayments',
					}),
				key: 'repayments',
			},
			{
				title: (intl) =>
					intl.formatMessage({
						defaultMessage: 'Korkokulut',
						description: 'Interest expenses',
					}),
				key: 'interestExpenses',
			},
			{
				title: (intl) =>
					intl.formatMessage({
						defaultMessage: 'Muut kulut',
						description: 'Other expenses',
					}),
				key: 'otherExpenses',
			},
			{
				title: (intl) =>
					intl.formatMessage({
						defaultMessage: 'Korkoprosentti',
						description: 'Interest percentage',
					}),
				key: 'interestPercentage',
			},
		],
	},
	{
		blockTitle: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Yli- ja alijäämä laskelma',
				description: 'Surplus and deficits calculation',
			}),
		key: 'surplus-and-deficit',
		rows: [
			{
				title: (intl, isDeficit) =>
					isDeficit
						? intl.formatMessage({
								defaultMessage: 'Alijäämä',
								description: 'Deficit',
						  })
						: intl.formatMessage({
								defaultMessage: 'Ylijäämä',
								description: 'Surplus',
						  }),
				key: 'baseBalance',
			},
			{
				title: (intl, isDeficit) =>
					isDeficit
						? intl.formatMessage({
								defaultMessage: 'Siirretty alijäämä',
								description: 'Deficit',
						  })
						: intl.formatMessage({
								defaultMessage: 'Siirretty ylijäämä',
								description: 'Surplus',
						  }),
				key: 'transferredBalance',
			},
			{
				title: (intl, isDeficit) =>
					isDeficit
						? intl.formatMessage({
								defaultMessage: 'Yhteensä alijäämä',
								description: 'Deficit',
						  })
						: intl.formatMessage({
								defaultMessage: 'Yhteensä ylijäämä',
								description: 'Surplus',
						  }),
				key: 'totalBalance',
			},
		],
	},
	{
		blockTitle: (intl) =>
			intl.formatMessage({
				defaultMessage: 'Summat per velallinen yksikkö',
				description: 'Sums per billing unit',
			}),
		key: 'sums-per-billing-unit',
		rows: [
			{
				title: (intl) =>
					intl.formatMessage({
						defaultMessage: 'Velalliset yksiköt',
						description: 'Units in debt',
					}),
				key: 'totalUnits',
			},
			{
				title: (intl) =>
					intl.formatMessage({
						defaultMessage: 'Lainaosuus',
						description: 'Loan share',
					}),
				key: '',
			},
			{
				title: (intl, isDeficit) =>
					isDeficit
						? intl.formatMessage({
								defaultMessage: 'Alijäämä',
								description: 'Deficit',
						  })
						: intl.formatMessage({
								defaultMessage: 'Ylijäämä',
								description: 'Surplus',
						  }),
				key: 'shareBaseBalance',
			},
			{
				title: (intl) =>
					intl.formatMessage({
						defaultMessage: 'Yhteensä summa',
						description: 'Total sum',
					}),
				key: 'shareTotalBalance',
			},
		],
	},
]
